import React from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { tokens } from '../../theme/tokens';

interface SectionCardProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Icon or count shown before the title. */
  leading?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  /**
   * `dense` is the data-page card: a 14px title on a hairline-divided header
   * row and no content padding, so tables and lists run edge to edge. The
   * default is the roomy settings card.
   */
  dense?: boolean;
  /** Pad the dense body; for prose or a form rather than a table. */
  padded?: boolean;
  sx?: React.ComponentProps<typeof Card>['sx'];
}

/**
 * The one titled card. Brand Home copied the same header markup into eight
 * cards and Brands & Vendors had its own `BvCard` + `BvCardHeader` pair.
 */
export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  subtitle,
  leading,
  actions,
  children,
  dense = false,
  padded = false,
  sx,
}) => {
  const hasHeader = Boolean(title || actions);

  if (dense) {
    return (
      <Card sx={{ overflow: 'hidden', minWidth: 0, maxWidth: '100%', ...sx }}>
        {hasHeader && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1.5}
            sx={{ px: '20px', py: '13px', borderBottom: `1px solid ${tokens.line.hairline}` }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              {leading}
              <Box sx={{ minWidth: 0 }}>
                {title && (
                  <Typography
                    component="div"
                    sx={{ fontSize: 14.5, fontWeight: 700, lineHeight: 1.3 }}
                    // A composite title (a stack with a chip) must be free to shrink and wrap.
                    noWrap={typeof title === 'string'}
                  >
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Stack>
            {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
          </Stack>
        )}
        {/*
          The card clips for its rounded corners, so the body scrolls horizontally
          itself: a wide table stays reachable instead of being cut off.
        */}
        <Box sx={{ minWidth: 0, overflowX: 'auto', ...(padded ? { p: '16px 20px' } : {}) }}>
          {children}
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={sx}>
      {hasHeader && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ px: 3, pt: 2.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {leading}
            <Stack spacing={0.25}>
              {title && (
                <Typography variant="h6" component="div">
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="body2" color="text.secondary" component="div">
                  {subtitle}
                </Typography>
              )}
            </Stack>
          </Stack>
          {actions}
        </Stack>
      )}
      <CardContent sx={{ p: 3 }}>{children}</CardContent>
    </Card>
  );
};
